import { ExecutionResult } from '@gql/gql-types';

import { TestCase, TestCaseFromTemplate } from './types';

export const getInitialTestCases = (
  templateTestCases: TestCaseFromTemplate[],
): TestCase[] => {
  return templateTestCases.map((t) => ({
    id: t.id,
    input: t.input,
    expected: t.expectedOutput,
    status: 'pending',
  }));
};

export const processExecutionResult = (
  executionResult: Pick<ExecutionResult, 'stdout'>,
  templateTestCases: TestCaseFromTemplate[],
): { testCases: TestCase[]; consoleOutput: string } => {
  let testCases: TestCase[] = getInitialTestCases(templateTestCases);
  let consoleOutput = '';

  if (!executionResult.stdout) {
    return { testCases, consoleOutput };
  }

  try {
    const backendResults: {
      id: string;
      status: string;
      output?: unknown;
      stdout?: string;
      error?: string;
    }[] = JSON.parse(executionResult.stdout);

    testCases = templateTestCases.map((tc) => {
      const result = backendResults.find((r) => r.id === tc.id);
      if (!result) {
        return {
          id: tc.id,
          input: tc.input,
          expected: tc.expectedOutput,
          status: 'pending' as const,
        };
      }

      let status: TestCase['status'];
      switch (result.status) {
        case 'success':
          status = 'passed';
          break;
        case 'error':
          status = 'failed';
          break;
        case 'timeout':
          status = 'timeout';
          break;
        default:
          status = 'failed';
      }

      const actualOutput =
        result.output !== undefined ? result.output : result.stdout;

      return {
        id: tc.id,
        input: tc.input,
        expected: tc.expectedOutput,
        status,
        actual: actualOutput,
        error: result.error,
        stdout: result.stdout,
      };
    });
  } catch (error) {
    console.error('Failed to parse execution results:', error);
    // If we can't parse stdout as JSON, it could be raw print statements
    consoleOutput = executionResult.stdout;
  }

  return { testCases, consoleOutput };
};

export const getConsoleOutputFromExecutionOutput = (output: string): string => {
  try {
    const executionResult = JSON.parse(output);
    if (executionResult.stderr) {
      return executionResult.stderr;
    }
    // Only show stdout if it's not test results JSON
    if (executionResult.stdout && !executionResult.stdout.startsWith('[')) {
      return executionResult.stdout;
    }
  } catch {
    // Not a JSON string
  }
  return '';
};
